import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface ArticleLayoutProps {
  title: string;
  description: string;
  canonicalPath: string;
  category: string;
  heroImage: string;
  heroAlt: string;
  publishedDate: string;
  readTime: string;
  children: React.ReactNode;
  jsonLdArticle?: {
    authorName?: string;
    datePublished?: string;
    dateModified?: string;
    imageUrl?: string;
    publisherName?: string;
    publisherLogo?: string;
  };
}

export default function ArticleLayout({
  title,
  description,
  canonicalPath,
  category,
  heroImage,
  heroAlt,
  publishedDate,
  readTime,
  children,
  jsonLdArticle,
}: ArticleLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL}${canonicalPath}`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL}${canonicalPath}`} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={heroImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="525" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={heroImage} />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: description,
        image: jsonLdArticle?.imageUrl || heroImage,
        author: {
          '@type': 'Organization',
          name: jsonLdArticle?.authorName || 'VigorBuy Spreadsheet Hub',
        },
        publisher: {
          '@type': 'Organization',
          name: jsonLdArticle?.publisherName || 'VigorBuy Spreadsheet Hub',
          logo: jsonLdArticle?.publisherLogo ? { '@type': 'ImageObject', url: jsonLdArticle.publisherLogo } : undefined,
        },
        datePublished: jsonLdArticle?.datePublished || '2026-07-15',
        dateModified: jsonLdArticle?.dateModified || '2026-07-23',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${import.meta.env.VITE_SITE_URL}${canonicalPath}`,
        },
      })}</script>

      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="font-heading font-bold text-lg text-foreground-950 whitespace-nowrap cursor-pointer"
            >
              VigorBuy Hub
            </Link>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap">Home</Link>
              <Link to="/article/vigorbuy-spreadsheet-complete-guide" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap">Shopping Guide</Link>
              <Link to="/article/vigorbuy-spreadsheet-safety-guide" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap">Safety</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap">Vigorbuy Spreadsheet</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer">Buy It Now</a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors duration-200"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center`}></i>
            </button>
          </div>

          {mobileOpen && (
            <div className="md:hidden bg-white rounded-xl border border-background-200 shadow-lg mt-2 p-4 mb-4">
              <div className="flex flex-col gap-1">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg cursor-pointer transition-colors">
                  <i className="ri-home-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  Home
                </Link>
                <Link to="/article/vigorbuy-spreadsheet-complete-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg cursor-pointer transition-colors">
                  <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  Shopping Guide
                </Link>
                <Link to="/article/vigorbuy-spreadsheet-safety-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg cursor-pointer transition-colors">
                  <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  Safety Guide
                </Link>
                <div className="border-t border-background-100 my-2"></div>
                <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</p>
                {[
                  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
                  { name: 'Hoodies / Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', icon: 'ri-t-shirt-line' },
                  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', icon: 'ri-shirt-line' },
                  { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', icon: 'ri-star-line' },
                  { name: 'Pants / Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html', icon: 'ri-pantone-line' },
                  { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', icon: 'ri-shopping-bag-3-line' },
                  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', icon: 'ri-briefcase-line' },
                  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html', icon: 'ri-contrast-drop-line' },
                ].map((cat) => (
                  <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-50 rounded-lg cursor-pointer transition-colors">
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                    {cat.name}
                  </a>
                ))}
                <div className="border-t border-background-100 my-2"></div>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg cursor-pointer transition-colors">
                  <i className="ri-external-link-line w-4 h-4 flex items-center justify-center text-accent-500"></i>
                  Visit Main Platform
                </a>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 mt-2 bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer">Buy It Now</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      <article className="pt-24">
        <header className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer inline-flex items-center gap-1">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
            <span className="text-foreground-300 text-sm">/</span>
            <span className="text-sm text-foreground-500">{category}</span>
          </div>
          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wide uppercase mb-4">{category}</span>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">{title}</h1>
          <p className="text-foreground-500 text-sm">Updated: {publishedDate} · {readTime}</p>
        </header>

        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="aspect-[16/7] rounded-2xl overflow-hidden mb-10">
            <img
              src={heroImage}
              alt={heroAlt}
              title={title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="prose-custom text-foreground-700 leading-relaxed">
            {children}
          </div>
        </div>
      </article>

      <footer className="bg-foreground-950 text-white mt-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="font-heading font-bold text-lg cursor-pointer">VigorBuy Hub</Link>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors duration-200 cursor-pointer">Home</Link>
              <Link to="/article/vigorbuy-spreadsheet-complete-guide" className="text-sm text-white/60 hover:text-white transition-colors duration-200 cursor-pointer">Shopping Guide</Link>
              <Link to="/article/vigorbuy-spreadsheet-safety-guide" className="text-sm text-white/60 hover:text-white transition-colors duration-200 cursor-pointer">Safety</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-white/60 hover:text-white transition-colors duration-200 cursor-pointer">Main Platform</a>
            </div>
          </div>
          <p className="text-xs text-white/40 text-center mt-6">
            &copy; {new Date().getFullYear()} Vigorbuy Spreadsheet Hub. Independent informational resource.
          </p>
        </div>
      </footer>
    </>
  );
}